/**
 * TabbedPanelInterface - A slightly opinionated component for rendering our
 * tabbed interfaces where tabs are associated with routes
 *
 * Takes as children <TabbedPanel>s
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, Navigate, Redirect, Routes, BrowserRouter } from 'react-router-dom';
import { HistoryRouter } from 'redux-first-history/rr6';
import map from 'lodash/fp/map';

import TabbedPanel from './TabbedPanel';
import TabbedView from './TabbedView';

import './index.scss';

class TabbedPanelInterface extends Component {
  static propTypes = {
    // base path to use for route
    basePath: PropTypes.string.isRequired,

    // a list of TabbedPanels to render
    children: PropTypes.array,

    // default path to redirect to when initially rendering the component
    defaultPath: PropTypes.string,

    // function to call to get default path; used if defaultPath is not passed in
    defaultPathSelector: PropTypes.func,

    // redux store to use for managing route changes
    store: PropTypes.object.isRequired,

    // allows you to use an alternate history (such if you want a redux based one that we do in adminUsersV2)
    history: PropTypes.object,
  };

  constructor(props) {
    super(props);

    const { store } = props;
  }

  render() {
    const { basePath, children, defaultPath, defaultPathSelector, store, history } = this.props;

    const state = store.getState();

    // create a list of panel configs for all the TabbedPanels we have as children
    const panelConfigs = React.Children.map(children, ({ props }) =>
      /*
       * TabbedPanel can either be passed some props (i.e. isVisible, path) directly
       * or given selector functions that grab things from state
       *
       * We cannot directly connect these to state since Router requires a list of Route components
       * and doesn't know how to traverse children to find Routes
       */
      TabbedPanel.connectTabPanelToState(state, props),
    );

    const RouterComponent = history ? HistoryRouter : BrowserRouter;
    const routerProps = history ? { history } : {};

    return (
      <RouterComponent {...routerProps}>
        <Routes>
          {/* Generate routes for each visible panel */}
          {panelConfigs
            .filter((config) => config.isVisible !== false) // Only show visible panels
            .map((config) => (
              <Route
                exact
                key={config.path}
                path={config.path}
                element={
                  <TabbedView panelConfigs={panelConfigs}>
                    <config.component />
                  </TabbedView>
                }
              />
            ))}
          <Route
            path={basePath}
            element={<Navigate to={defaultPath || defaultPathSelector(state)} replace />}
          />
        </Routes>
      </RouterComponent>
    );
  }
}

export default TabbedPanelInterface;
